import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "~/lib/ui/utils/utils";

function TooltipContent({
  className,
  sideOffset = 8,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Content
      className={cn(
        `
          z-50 max-w-64 overflow-hidden rounded-lg border bg-popover px-3
          py-1.5 text-sm text-popover-foreground shadow-md

          data-[state=closed]:animate-out data-[state=closed]:fade-out-0
          data-[state=open]:animate-in data-[state=open]:fade-in-0
          data-[state=open]:zoom-in-95
        `,
        className,
      )}
      sideOffset={sideOffset}
      {...props}
    />
  );
}

const TooltipProvider = TooltipPrimitive.Provider;

type TooltipProps = {
  children: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
  /**
   * Controls the tooltip imperatively instead of via hover. Pass a boolean to
   * show/hide (e.g. a "Copied!" confirmation); leave undefined for the
   * default hover behavior.
   */
  open?: boolean;
  side?: "top" | "right" | "bottom" | "left";
  contentClassName?: string;
  /**
   * Class for the inline-flex trigger span wrapping the children. Pass
   * "w-full" when wrapping block-level content (sliders, inputs) so the
   * trigger doesn't collapse the child's width.
   */
  triggerClassName?: string;
};

function Tooltip({
  children,
  content,
  disabled = false,
  open,
  side,
  contentClassName,
  triggerClassName,
}: TooltipProps) {
  if (disabled || content == null) {
    return <>{children}</>;
  }

  return (
    <TooltipProvider delayDuration={150} skipDelayDuration={100}>
      <TooltipPrimitive.Root open={open}>
        <TooltipPrimitive.Trigger asChild>
          <span className={cn("inline-flex", triggerClassName)}>
            {children}
          </span>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipContent side={side ?? "top"} className={contentClassName}>
            {content}
          </TooltipContent>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipProvider>
  );
}

export { Tooltip };
